import _isEqual from 'lodash/isEqual'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { memo, useState } from 'react'
import { View } from 'react-native'

import { SupportedMarketKey } from '@nxt-insurance/constants'
import logo from '@nxt-insurance/ui-kit/assets/images/src/illustrations/logo_green_41x34.svg'
import logoWordMark from '@nxt-insurance/ui-kit/assets/images/src/illustrations/logo_wordmark_130x16.svg'
import ButtonIcon from '@nxt-insurance/ui-kit/components/ButtonIcon'
import GlobalHeader from '@nxt-insurance/ui-kit/components/GlobalHeader'

import type { Navbar, Route } from '@/cms/pages'
import { Icon } from '@/components/Icon'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { NavigationMenu } from '@/components/sections/Header/NavigationMenu'
import { EVENTS, LINKS_IDS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import type { AlternateLink } from '@/hooks/useAlternateLinks'
import { getFromCookiesOrUrlParams, queryToSearchParams } from '@/utils/cookies'
import { getLocaleParts } from '@/utils/locale'
import { h4 } from '@ui-kit/core/typography'

import { headerStyles } from './styles'

const CampaignBar = dynamic(async () =>
  import('@/components/sections/Header/CampaignBar').then((mod) => mod.CampaignBar)
)

const SwitchToEnglish = dynamic(async () =>
  import('@/components/sections/Header/SwitchToEnglish').then((mod) => mod.SwitchToEnglishSection)
)

const TravelHealthNowAvailable = dynamic(async () =>
  import('@/components/sections/Header/TravelHealthNowAvailable').then(
    (mod) => mod.TravelHealthNowAvailable
  )
)

type Props = {
  pageType: 'regular' | 'error' | 'not_found'
  navbar?: Navbar
  routePageType?: Route['pageType']
  alternateLinks: AlternateLink[]
}

const Header = (props: Props) => {
  const { track } = useAnalytics()
  const { query, locale, isPreview } = useRouter()
  const { campaign } = getFromCookiesOrUrlParams(queryToSearchParams(query))
  const [lang, market] = getLocaleParts(locale as string)

  const { navbar: siteSettingsNavbar } = props

  const navbarItems = siteSettingsNavbar?.items ?? []
  const { t } = useTranslation()
  const [isMainMenuVisible, setMainMenuVisibility] = useState(false)

  const toggleMainMenuVisibility = () =>
    setMainMenuVisibility((currentVisibility) => !currentVisibility)
  const logoHref = `/${lang}-${market}`

  const shouldNavbarItemsBeDisplayed =
    navbarItems && navbarItems.length > 0 && props.pageType === 'regular'

  // TODO: AS-269 turn it on when the travel health campaign is ready
  // If there is no campaign, we should show the travel health waiting list. Otherwise, the list is overwritten by the campaign bar.
  const shouldShowTravelHealthNowAvailable =
    campaign === undefined && market === SupportedMarketKey.De

  const styles = headerStyles({
    isMainMenuVisible,
    hasCampaign: Boolean(campaign) || market === SupportedMarketKey.De,
  })

  return (
    <>
      {isMainMenuVisible ? (
        <style global jsx>{`
          body {
            overflow: hidden;
            height: 100vh;
          }
        `}</style>
      ) : null}
      <View className={styles.headerContainer()}>
        {isPreview ? (
          <View className={styles.previewHeaderContainer()}>
            <View className={styles.previewHeader()}>
              <span className={h4()}>Preview mode</span>
              <Link
                href="/api/exit-preview"
                aria-label="Exit"
                prefetch={false}
                className={styles.previewExitLink()}
              >
                <span>Exit</span>
                <Icon size="small" icon={{ name: 'action.close_24' }} />
              </Link>
            </View>
          </View>
        ) : null}
        {campaign ? <CampaignBar campaign={campaign} /> : null}
        <TravelHealthNowAvailable shouldShow={shouldShowTravelHealthNowAvailable} />
        <GlobalHeader
          logoLink={{
            href: logoHref,
            onPress: () =>
              track(EVENTS.LinkClicked, {
                linkId: LINKS_IDS.HEADER_NAVIGATION_LOGO_TO_INDEX,
                href: logoHref,
              }),
          }}
          wordMark={
            <Link href={logoHref} className={styles.wordMark()}>
              <Image
                src={logoWordMark}
                alt="Getsafe wordmark"
                unoptimized
                fill
                priority
                loader={({ src }) => src}
              />
            </Link>
          }
          logo={
            <Link href={logoHref} className={styles.logo()}>
              <Image
                src={logo}
                alt="Getsafe logo"
                fill
                priority
                loader={({ src }) => src}
                unoptimized
              />
            </Link>
          }
        >
          <View className={styles.navbarContainer()}>
            <View className={styles.navbar()}>
              <View className={styles.languageSwitcherContainer()}>
                <LanguageSwitcher
                  onPress={() => setMainMenuVisibility(false)}
                  alternateLinks={props.alternateLinks}
                />
              </View>
              <View className={styles.navbarButton()}>
                <ButtonIcon
                  onPress={toggleMainMenuVisibility}
                  icon={
                    <Image
                      src={'/assets/images/svg/icon-close.svg'}
                      alt={t('icon.close')}
                      width={16}
                      height={16}
                      loader={({ src }) => src}
                      unoptimized
                    />
                  }
                />
              </View>
            </View>

            {shouldNavbarItemsBeDisplayed ? (
              <NavigationMenu
                closeMenu={() => setMainMenuVisibility(false)}
                items={navbarItems}
                hasCampaign={Boolean(campaign) || market === SupportedMarketKey.De}
              />
            ) : null}
          </View>
          {shouldNavbarItemsBeDisplayed ? (
            <View className={styles.navbarButton()}>
              <ButtonIcon
                testID="CY_header__burger"
                onPress={toggleMainMenuVisibility}
                icon={
                  <Image
                    src="/assets/images/svg/icon_burger.svg"
                    width={21}
                    height={16}
                    alt={t('icon.menu')}
                    loader={({ src }) => src}
                    unoptimized
                  />
                }
              />
            </View>
          ) : null}
        </GlobalHeader>
      </View>
      {!shouldShowTravelHealthNowAvailable && campaign === undefined ? (
        <SwitchToEnglish alternateLinks={props.alternateLinks} />
      ) : null}
    </>
  )
}

export default memo(Header, _isEqual)
